// Login.js

import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';

function Admin() {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [authLevel, setAuthLevel] = useState('');
    const [message, setMessage] = useState('');
    const [isLogin, setIsLogin] = useState(true);
    const { loggedIn, setLoggedIn, loggedUser, adminUser, setAdminUser, adminAuthLevel, setAdminAuthLevel } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState('pw-admin');

    //Customer Search
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [resetUser, setResetUser] = useState('');

    // Min Enrollments 
    const [minEnrollments, setMinEnrollments] = useState([]);

    // Auth Levels
    const [authLevels, setAuthLevels] = useState([]);

    //const [originalEnrollments] = useState(initialMinEnrollments); // deep copy for reset
    const [saveStatus, setSaveStatus] = useState('idle');
    const [saveMessage, setSaveMessage] = useState('');

    useEffect(() => {
        // Check if the user is logged in when the component mounts
        checkSession();
        fetchMinEnrollemnts();
        fetchAuthLevels();
    }, []);

    const checkSession = async () => {
        try {
            const { data } = await axios.get('/api/check-session', { withCredentials: true });
            setLoggedIn(data.loggedIn);
            if (!data.loggedIn) {
                navigate('/Home');
            }
            const params = new URLSearchParams({ userId: data.user.id });
            const response = await fetch(`/api/checkAdmin?${params}`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const adminData = await response.json();
            if (adminData.isAdmin) {
                setAdminUser(adminData.isAdmin);
                setAdminAuthLevel(adminData.auth_level);
            } else {
                setAdminUser(false);
                setAdminAuthLevel(null);
                navigate('/Home');
            }
        } catch (error) {
            console.error('Session check failed:', error);
        }
    };

    const fetchMinEnrollemnts = async () => {
        try {
            const { data } = await axios.get('/api/minEnrollment', { withCredentials: true });
            if (data) {
                setMinEnrollments(data);
            }
        } catch (error) {
            console.error('Could not fetch min enrollments:', error);
        }
    };

    const fetchAuthLevels = async () => {
        try {
            const params = new URLSearchParams({ userId: loggedUser, authLevel: adminAuthLevel });
            const { data } = await axios.get(`/api/auth-levels?${params}`, { withCredentials: true });
            if (data) {
                setAuthLevels(data);
            }
        } catch (error) {
            console.error('Could not fetch auth levels:', error);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const endpoint = '/api/register';
        try {
            const { data } = await axios.post(endpoint, { username, password, email, authLevel }, { withCredentials: true });
            setMessage(data.message);
            setUsername('');
            setPassword('');
            setEmail('');
            setAuthLevel(1);
        } catch (error) {
            setMessage('Failed: ' + (error.response?.data?.error || error.message));
        }
    };

    const tabs = [
        { id: 'pw-admin', label: 'PASSWORD RESET' },
        { id: 'user-admin', label: 'USER ADMINISTRATION' },
        { id: 'min-enrollments', label: 'ENROLLMENT RULES' }
    ];

    const handleMinEnrollmentChange = function (product_id, outlet_type, value) {
        var numValue = value.trim() === '' ? null : Number(value);

        setMinEnrollments(function (prev) {
            return prev.map(function (row) {
                if (row.product_id === product_id && row.outlet_type === outlet_type) {
                    return { ...row, min_enrollment: numValue };
                }
                return row;
            });
        });
    };

    const handleSaveEnrollments = function () {
        setSaveStatus('saving');
        setSaveMessage('');

        // Defensive check
        if (!Array.isArray(minEnrollments)) {
            setSaveStatus('error');
            setSaveMessage('Internal error: data is not an array');
            return;
        }

        fetch('/api/updateEnrollments', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // 'Authorization': 'Bearer ' + yourToken,   // ← add if you have auth
            },
            body: JSON.stringify({
                rules: minEnrollments.map(row => ({
                    product_id: row.product_id,
                    outlet_type: row.outlet_type,
                    min_enrollment: row.min_enrollment   // already null or number
                }))
            })
        })
            .then(function (response) {
                if (!response.ok) {
                    return response.json().then(function (errData) {
                        throw new Error(errData.error || 'Save failed – status ' + response.status);
                    });
                }
                return response.json();
            })
            .then(function (result) {
                setSaveStatus('success');
                setSaveMessage(result.message || 'Rules saved successfully');
                // Optional: you could refresh data from server here
            })
            .catch(function (err) {
                setSaveStatus('error');
                setSaveMessage(err.message || 'Could not save changes');
                console.error('Save failed:', err);
            });
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        if (event.target.value) {
            fetchSearchResults(event.target.value);
            setShowResults(true); // Show results when typing
        } else {
            setShowResults(false); // Hide results when search term is empty
            setSearchResults([]);
        }
    };

    // Fetch search results from backend
    const fetchSearchResults = async (term) => {
        if (!term || term.length < 3) {
            return;
        }
        try {
            const response = await fetch(`/api/userSearch?query=${encodeURIComponent(term)}`);
            const data = await response.json();
            setSearchResults(data); // Update with server results
        } catch (error) {
            console.error('Error fetching search results:', error);
        }
    };

    const handleItemClick = (customer) => {
        setSearchTerm(customer.user_name || customer.user_id); // Update search term with selected customer's name
        setResetUser(customer.user_id);
        setSearchResults([]); // Clear results
        setShowResults(false); // Hide the results list
    };

    const handleReset = async (event) => {
        event.preventDefault();
        if (!resetUser || !password) {
            setMessage('User and Password must be provided')
            return;
        }
        const endpoint = '/api/update-user';
        try {
            const { data } = await axios.post(endpoint, { resetUser, password }, { withCredentials: true });
            setMessage(data.message);
            setPassword('');
            setSearchTerm('');
        } catch (error) {
            setMessage('Failed: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleTest = async (event) => {
        event.preventDefault();
        try {
            const response = await axios.post('/api/ds-test');
            const data = response.data;
            console.log(data);
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <div className="admin-dashboard">
            <div className="tabs-header">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="tab-content">
                {activeTab === 'pw-admin' && (
                    <div className="user-form">
                        <h2>PASSWORD RESET</h2>
                        <form className='admin-form' onSubmit={handleReset}>
                            <div>
                                <input
                                    type="text"
                                    id="username"
                                    placeholder="Search for a Customer"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    required
                                />
                                {showResults && searchResults.length > 0 && (
                                    <ul className="search-results">
                                        {searchResults.map((customer, index) => (
                                            <li
                                                key={index}
                                                onClick={() => handleItemClick(customer)}
                                            >
                                                {customer.user_name ? customer.user_name : customer.user_id}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div>
                                <input
                                    type="password"
                                    id="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <button type="submit">RESET PASSWORD</button>
                        </form>

                        {/* <button onClick={handleTest}>TEST DS</button> */}

                        <div className={message ? 'message visible' : 'message hidden'}>
                            <p>{message}</p>
                        </div>
                    </div>
                )}

                {activeTab === 'user-admin' && (
                    <div className="user-form">
                        <h2>USER ADMINISTRATION</h2>
                        <form className='admin-form' onSubmit={handleSubmit}>
                            <div>
                                <input
                                    type="text"
                                    id="username"
                                    placeholder="Username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    maxLength={12}
                                    required
                                />
                            </div>
                            <div>
                                <input
                                    type="password"
                                    id="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <input
                                    type="email"
                                    id="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <select
                                    name="authLevel"
                                    value={authLevel}
                                    onChange={(e) => setAuthLevel(e.target.value)}
                                    required
                                >
                                    {authLevels.map((authLevel, index) => (
                                        <option value={authLevel.auth_level}>{authLevel.auth_name}</option>
                                    ))}
                                </select>
                            </div>
                            <button type="submit">CREATE USER</button>
                        </form>

                        <div className={message ? 'message visible' : 'message hidden'}>
                            <p>{message}</p>
                        </div>
                    </div>
                )}

                {activeTab === 'min-enrollments' && (
                    <div className="min-enrollments">
                        <h2>ENROLLMENT RULES</h2>
                        <table className="rules-table">
                            <thead>
                                <tr>
                                    <th>PRODUCT</th>
                                    <th>OUTLET TYPE</th>
                                    <th>MIN ENROLLMENT (DAYS)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {minEnrollments.map((row, idx) => (
                                    <tr key={`${row.product_id}-${row.outlet_type}-${idx}`}>
                                        <td className="product-cell">{row.product_name}</td>
                                        <td className="outlet-cell">{row.description}</td>
                                        <td className="input-cell">
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={row.min_enrollment ?? ''}
                                                placeholder="—"
                                                onChange={function (e) {
                                                    handleMinEnrollmentChange(row.product_id, row.outlet_type, e.target.value);
                                                }}
                                                disabled={saveStatus === 'saving'}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Optional: save button for enrollments */}
                        <div className="actions">
                            <button
                                className="actionButton"
                                onClick={handleSaveEnrollments}
                                disabled={saveStatus === 'saving'}
                            >
                                {saveStatus === 'saving' ? 'SAVING...' : 'SAVE CHANGES'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Future tabs would go here as additional conditions */}
            </div>
        </div>
    );
}

export default Admin;